import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Sparkle } from "@phosphor-icons/react";
import { api, streamAi } from "@/lib/api";

const KINDS = [
  ["reply", "Reply"],
  ["follow_up", "Follow-up"],
  ["sms", "Text message"],
];

// "Draft a reply" for an enquiry — streams the model's words into an editable box.
export const DraftReply = ({ leadId, email }) => {
  const [on, setOn] = useState(false);
  const [kind, setKind] = useState("reply");
  const [extra, setExtra] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/settings/ai-text")
      .then(({ data }) => setOn(data.config.enabled && data.config.drafting))
      .catch(() => setOn(false));
  }, []);

  if (!on) return null;

  const run = async () => {
    setBusy(true);
    setText("");
    let out = "";
    try {
      await streamAi(
        "/ai/draft",
        { kind, lead_id: leadId, extra },
        (delta) => {
          out += delta;
          setText(out);
        },
      );
    } catch (err) {
      toast.error(err.message || "Could not write a draft");
    }
    setBusy(false);
  };

  return (
    <div
      className="mt-6 rounded-lg border border-zinc-800 bg-zinc-950 p-4"
      data-testid="lead-draft-panel"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-widest text-zinc-400">
          Draft with AI
        </span>
        <div className="ml-auto flex flex-wrap gap-2">
          {KINDS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              data-testid={`draft-kind-${value}`}
              onClick={() => setKind(value)}
              className={`h-9 rounded-lg px-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                kind === value
                  ? "bg-zinc-100 text-zinc-900"
                  : "border border-zinc-800 bg-zinc-900 text-zinc-400 hover:text-zinc-100"
              }`}
            >
              {label}
            </button>
          ))}
          <button
            type="button"
            data-testid="draft-run"
            disabled={busy}
            onClick={run}
            className="flex h-9 items-center gap-2 rounded-lg bg-indigo-500 px-4 text-[10px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400 disabled:opacity-50"
          >
            <Sparkle size={13} weight="bold" />
            {busy ? "Writing…" : "Write it"}
          </button>
        </div>
      </div>

      <input
        data-testid="draft-extra"
        value={extra}
        onChange={(e) => setExtra(e.target.value)}
        placeholder="Anything to include? e.g. quote by Friday, 3cm quartzite, ask for a photo"
        className="mt-3 w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2.5 text-sm text-zinc-200 outline-none focus:border-indigo-500"
      />

      {(text || busy) && (
        <>
          <textarea
            data-testid="draft-text"
            rows={8}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="mt-3 w-full rounded-lg border border-zinc-800 bg-zinc-900 p-3 text-sm leading-relaxed text-zinc-200 outline-none focus:border-indigo-500"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="draft-copy"
              onClick={() => {
                navigator.clipboard?.writeText(text);
                toast.success("Copied");
              }}
              className="flex h-9 items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 px-4 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:text-white"
            >
              <Copy size={13} weight="bold" /> Copy
            </button>
            {email && kind !== "sms" && (
              <a
                data-testid="draft-mailto"
                href={`mailto:${email}?subject=${encodeURIComponent(
                  "Your enquiry",
                )}&body=${encodeURIComponent(text)}`}
                className="flex h-9 items-center rounded-lg border border-zinc-800 bg-zinc-900 px-4 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:text-white"
              >
                Open in email
              </a>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DraftReply;
